import React, { useEffect, useRef } from 'react'
import type { FC } from 'react'
import type { PDPProps } from 'src/pages/[slug]/p'
import type { BrowserProductQueryQuery } from '@generated/graphql'
import { useProductViewContext } from 'src/contexts/product-view-context'
import { useSession } from 'src/sdk/session'
import { useViewItem } from 'src/sdk/analytics/hooks/useViewItemEvent'
import ProductDetails from 'src/components/sections/ProductDetails/ProductDetails'
import { formatAnalyticsData } from 'src/utils/formatAnalyticsData'

interface Props extends PDPProps {
  product: BrowserProductQueryQuery['product']
  selectedSKU: BrowserProductQueryQuery['product']
}

const AboveTheFold: FC<Props> = ({ product, selectedSKU }) => {
  const hasSentEvent = useRef(false)
  const { filteredSlas, postalCodeSlas, slasReady, isUpdatedSlas, skuIdOfSlas } = useProductViewContext()
  const { person } = useSession()
  const { sendUseViewItemEvent } = useViewItem()
  const hasStock = product?.offers?.offers?.[0]?.availability === 'https://schema.org/InStock'

  useEffect(() => {
    hasSentEvent.current = false
  }, [selectedSKU?.sku])

  useEffect(() => {
    if (hasSentEvent.current || !selectedSKU?.sku) {
      return
    }

    const hasPostalCode = Boolean(postalCodeSlas)
    const isNoSlas = filteredSlas?.length === 0
    const slasNotReadyYet = !slasReady && !hasPostalCode

    const shouldSendEvent =
      (hasPostalCode && slasReady && isUpdatedSlas && skuIdOfSlas === selectedSKU?.sku) ||
      (!hasPostalCode && hasStock && isNoSlas && slasNotReadyYet) || // sem CEP, com estoque, SLAs ainda não prontos
      (!hasStock && isNoSlas) // sem estoque

    if (!shouldSendEvent) {
      return
    }

    const eventData = formatAnalyticsData(product, selectedSKU, filteredSlas, person)

    if (eventData) {
      sendUseViewItemEvent(eventData)
      hasSentEvent.current = true
    }
  }, [
    selectedSKU?.sku,
    filteredSlas,
    slasReady,
    isUpdatedSlas,
    postalCodeSlas,
    skuIdOfSlas,
    product,
    person,
  ])

  return (
    <>
      {/*
        Sections: Components imported from '../components/sections' only.
        Do not import or render components from any other folder in here.
      */}
      <ProductDetails product={product} />
    </>
  )
}

export default AboveTheFold
